import { z } from 'zod';

import { makeEndpoint } from '../common';

const FeatureAlertSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  description: z.string(),
  buttonText: z.string().nullable().optional(),
  titleLink: z.string().url().nullable().optional(),
  buttonLink: z.string().nullable().optional(),
  releaseDate: z.string().datetime(),
  badgeText: z.string().nullable().optional(),
  showBadge: z.boolean(),
  expiresAt: z.string().datetime(),
  brandIds: z.array(z.string().uuid()),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

const CreateFeatureAlertRequestSchema = z.object({
  title: z.string().min(1),
  description: z.string().min(1),
  buttonText: z.string().optional(),
  titleLink: z.string().url().optional(),
  buttonLink: z.string().optional(),
  releaseDate: z.string().datetime(),
  badgeText: z.string().optional(),
  showBadge: z.boolean().optional(),
  expiresAt: z.string().datetime().optional(),
  brandIds: z.array(z.string().uuid()).optional(),
});

const UpdateFeatureAlertRequestSchema = z.object({
  title: z.string().optional(),
  description: z.string().optional(),
  buttonText: z.string().optional(),
  titleLink: z.string().url().optional(),
  buttonLink: z.string().optional(),
  releaseDate: z.string().datetime().optional(),
  badgeText: z.string().optional(),
  showBadge: z.boolean().optional(),
  expiresAt: z.string().datetime().optional(),
  brandIds: z.array(z.string().uuid()).optional(),
});

const GetFeatureAlertsQuerySchema = z.object({
  includeExpired: z
    .string()
    .optional()
    .transform((val) => val === 'true'),
});

export type FeatureAlert = z.output<typeof FeatureAlertSchema>;
export type CreateFeatureAlertRequest = z.input<typeof CreateFeatureAlertRequestSchema>;
export type UpdateFeatureAlertRequest = z.input<typeof UpdateFeatureAlertRequestSchema>;
export type GetFeatureAlertsQuery = z.input<typeof GetFeatureAlertsQuerySchema>;

export const CreateFeatureAlertEndpoint = makeEndpoint({
  method: 'POST',
  url: () => 'v1/feature-alerts',
  responseSchema: FeatureAlertSchema,
  requestSchema: CreateFeatureAlertRequestSchema,
});

export const GetAllFeatureAlertsEndpoint = makeEndpoint({
  method: 'GET',
  url: () => 'v1/feature-alerts',
  responseSchema: z.array(FeatureAlertSchema),
  requestSchema: z.void(),
  querySchema: GetFeatureAlertsQuerySchema,
});

export const GetActiveFeatureAlertsEndpoint = makeEndpoint({
  method: 'GET',
  url: () => 'v1/feature-alerts/active',
  responseSchema: z.array(FeatureAlertSchema),
  requestSchema: z.void(),
});

export const GetFeatureAlertEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ id }: { id: string }) => `v1/feature-alerts/${id}`,
  responseSchema: FeatureAlertSchema,
  requestSchema: z.void(),
});

export const UpdateFeatureAlertEndpoint = makeEndpoint({
  method: 'PUT',
  url: ({ id }: { id: string }) => `v1/feature-alerts/${id}`,
  responseSchema: FeatureAlertSchema,
  requestSchema: UpdateFeatureAlertRequestSchema,
});

export const DeleteFeatureAlertEndpoint = makeEndpoint({
  method: 'DELETE',
  url: ({ id }: { id: string }) => `v1/feature-alerts/${id}`,
  responseSchema: z.void(),
  requestSchema: z.void(),
});
